/* eslint-disable react/require-default-props */
import * as React from 'react';
import { Icon, LabelButton, Select, Status } from '@teamsnap/snap-ui';

import { AccountStatus } from 'components/organization/organization-accounts-management';
import { RegType } from 'components/pages/season-financials';
import Organization from 'models/organization';
import SeasonMerchantAccountUnapprovedModal from './SeasonMerchantAccountUnapprovedModal';

import './SeasonMerchantAccount.scss';

interface Props {
  currentAccount: any;
  accounts: any;
  onSave: (account?: any) => void;
  editable: boolean;
  organization: Organization;
  programId: number;
  seasonId: number;
  onAddAccount: () => void;
  onVerifyOrConnect: (currentAccount?) => Promise<void>;
  disabled: boolean;
  regType: RegType;
}

const AccountOption = ({ account, isSelected = false, children = null, hasHover = false }) => (
  <div
    data-testid={`SeasonMerchantAccount--account-option-item-${account.id}`}
    className={`sui-flex-1 sui-flex sui-items-center sui-justify-between sui-p-1 sui-cursor-pointer ${
      hasHover ? 'hover:sui-bg-action-background-weak-hover' : ''
    }`}
    style={{ minHeight: 48 }}
  >
    <div className="sui-flex-1">
      <AccountOptionName account={account} children={children} />
      <AccountOptionLastFour account={account} />
    </div>
    <AccountOptionStatus account={account} />
    {isSelected && <Icon name="check" className="sui-text-action-icon sui-ml-1" />}
  </div>
);

const AccountOptionName = ({ account, children = null }) => (
  <div data-testid="SeasonMerchantAccount--account-option-name" className="sui-body-dense sui-text-neutral-text">
    {children || account?.nickname}
  </div>
);

const AccountOptionLastFour = ({ account }) => (
  <>
    {account?.bankAccountNumberLast4 && (
      <div
        data-testid="SeasonMerchantAccount--account-option-last-four"
        className="sui-caption sui-text-neutral-text-medium sui-pr-1 sui-mt-1"
      >
        *{account?.bankAccountNumberLast4}
      </div>
    )}
  </>
);

const AccountOptionStatus = ({ account }) => (
  <>
    {account?.status === AccountStatus.APPROVED && (
      <Status testId="SeasonMerchantAccount--account-option-status" state="success" text="Approved" />
    )}
    {account?.status === AccountStatus.RESTRICTED_SOON && (
      <Status testId="SeasonMerchantAccount--account-option-status" state="warning" text="Restricted soon" />
    )}
    {account?.status === AccountStatus.UNDER_REVIEW && (
      <Status testId="SeasonMerchantAccount--account-option-status" state="negative" text="Under review" />
    )}
    {account?.status === AccountStatus.INFO_NEEDED && (
      <Status testId="SeasonMerchantAccount--account-option-status" state="negative" text="Information required" />
    )}
    {account?.status === AccountStatus.BANK_DETAILS_REQUIRED && (
      <Status testId="SeasonMerchantAccount--account-option-status" state="negative" text="Bank details required" />
    )}
    {account?.status === AccountStatus.REJECTED && (
      <Status testId="SeasonMerchantAccount--account-option-status" state="negative" text="Rejected" />
    )}
  </>
);

const SeasonMerchantAccountHeading = (regType: string) => {
  const content = {
    heading: 'Selected Deposit Account',
    description: 'Fees collected for this season’s forms and invoices will be deposited to the selected account.',
  };
  if (regType === 'reg2' || regType === 'both') {
    content.heading = 'Invoice Deposit account';
    content.description = `Collected invoice payments will be deposited to this account. Collected fees from registration
      forms will be deposited into the account selected in the registration wizard, not this account.`;
  }
  return (
    <div className="sui-max-w-[768px]">
      <h2 className="sui-heading-md">{content.heading}</h2>
      <p className="sui-caption sui-text-gray-40">{content.description}</p>
    </div>
  );
};

const SeasonMerchantAccount: React.FC<Props> = ({
  accounts,
  onSave,
  currentAccount,
  editable,
  onAddAccount,
  onVerifyOrConnect,
  disabled,
  organization,
  regType
}) => {
  const [showEdit, setShowEdit] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [selectedAccount, setSelectedAccount] = React.useState(null);
  const [highlightedAccount, setHighlightedAccount] = React.useState(null);
  const [highlightedAccountForModal, setHighlightedAccountForModal] = React.useState(null);
  const [showMerchantSelectModal, setShowMerchantSelectModal] = React.useState(false);
  const [accountOptions, setAccountOptions] = React.useState([]);
  const [errors, setErrors] = React.useState([]);
  const [paymentAccountStatus, setPaymentAccountStatus] = React.useState(null);

  React.useEffect(() => {
    const selectedMerchantAccount = accounts?.find((account) => account.id === +highlightedAccountForModal);

    if (
      selectedMerchantAccount &&
      (selectedMerchantAccount.payoutsEnabled === false || selectedMerchantAccount.paymentsEnabled === false)
    ) {
      setPaymentAccountStatus(selectedMerchantAccount.status);
      setShowMerchantSelectModal(true);
    }
  }, [highlightedAccountForModal, accounts]);

  React.useEffect(() => {
    if (accounts) {
      const options = accounts.map((account) => ({
        value: String(account.id),
        label: account.nickname,
      }));
      setAccountOptions(options);
    }
  }, [accounts]);

  React.useEffect(() => {
    if (currentAccount) {
      setSelectedAccount(currentAccount);
      setHighlightedAccount(currentAccount.id);
    }
  }, [currentAccount]);

  React.useEffect(() => {
    if (!currentAccount && editable) {
      setShowEdit(true);
    } else {
      setShowEdit(false);
    }
  }, [currentAccount, editable]);

  React.useEffect(() => {
    if (currentAccount && !showEdit) {
      setSelectedAccount(currentAccount);
      setHighlightedAccount(currentAccount.id);
      setIsSubmitting(false);
    }
  }, [currentAccount, showEdit]);

  const CustomMenuList = ({ children, ...props }) => (
    <div {...props.innerProps}>
      <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
        <div data-testid="SeasonMerchantAccount--accounts-list">{children}</div>
        <div className="sui-p-1">
          <LabelButton
            icon="add"
            iconPosition="left"
            labelText="Add a new deposit account"
            sentiment="default"
            size="default"
            variantType="secondary"
            onClick={() => {
              onAddAccount();
            }}
          />
        </div>
      </div>
    </div>
  );

  const CustomPaymentAccountOption = ({ data, isSelected, innerProps, innerRef }) => {
    const account = accounts.find((a) => +a.id === +data.value);
    return (
      <div ref={innerRef} {...innerProps}>
        <AccountOption account={account} isSelected={isSelected} hasHover={true} />
      </div>
    );
  };

  const CustomValueContainer = ({ children, ...props }) => {
    let account;
    const selectedValue = props.getValue()[0]?.value || null;
    if (selectedValue) {
      account = accounts.find((a) => +a.id === +selectedValue);
      return <AccountOption account={account} children={children} />;
    } else {
      return (
        <div className="sui-flex sui-items-center sui-justify-between sui-p-1" style={{ minHeight: 48 }}>
          {children}
        </div>
      );
    }
  };

  const save = () => {
    if (
      (currentAccount && highlightedAccount && +currentAccount.id !== +highlightedAccount) ||
      (highlightedAccount && !currentAccount)
    ) {
      const account = accounts.find((a) => +a.id === +highlightedAccount);
      setIsSubmitting(true);
      setErrors([]);
      setSelectedAccount(account);
      onSave(account);
    } else if (
      (currentAccount && highlightedAccount && +currentAccount.id === +highlightedAccount) ||
      (currentAccount && !highlightedAccount)
    ) {
      setErrors([]);
      setShowEdit(false);
      onSave();
    } else {
      setErrors(['You must select a deposit account']);
    }
  };

  const handleVerifyOrConnectInModal = async () => {
    const currentlySelectedAccount = accounts?.find((a) => +a.id === +highlightedAccountForModal);

    if (currentlySelectedAccount) {
      await onVerifyOrConnect(currentlySelectedAccount);
    }
  };

  return (
    <>
      <SeasonMerchantAccountUnapprovedModal
        isOpen={showMerchantSelectModal}
        closeFn={() => setShowMerchantSelectModal(false)}
        orgId={organization?.id}
        onVerifyOrConnect={handleVerifyOrConnectInModal}
        paymentAccountStatus={paymentAccountStatus}
      />

      <div className="SeasonMerchantAccount__container" data-testid="SeasonMerchantAccount">
        {showEdit ? (
          <div className="sui-bg-neutral-background-weak sui-rounded sui-p-2 sui-max-w-[768px]">
            {SeasonMerchantAccountHeading(regType)}
            <div className="sui-my-2">
              <Select
                data-testid="SeasonMerchantAccount--account-select"
                name="account"
                onChange={(accountId) => {
                  setErrors([]);
                  setHighlightedAccount(accountId);
                  setHighlightedAccountForModal(accountId);
                }}
                options={accountOptions}
                value={highlightedAccount}
                placeholder="Select or create a deposit account"
                size="x-large"
                components={{
                  Option: CustomPaymentAccountOption,
                  ValueContainer: CustomValueContainer,
                  MenuList: CustomMenuList,
                }}
                errors={errors}
                disabled={isSubmitting}
              />
            </div>
            <div className="sui-flex">
              {selectedAccount && (
                <LabelButton
                  data-testid="SeasonMerchantAccount--cancel-button"
                  className="sui-mr-2"
                  variantType="secondary"
                  disabled={isSubmitting}
                  onClick={() => {
                    setErrors([]);
                    setShowEdit(false);
                  }}
                >
                  Cancel
                </LabelButton>
              )}
              <LabelButton data-testid="SeasonMerchantAccount--save-button" disabled={isSubmitting} onClick={save}>
                Save
              </LabelButton>
            </div>
          </div>
        ) : (
          <>
            {SeasonMerchantAccountHeading(regType)}
            <div className="sui-max-w-[768px] sui-my-2 sui-py-1 sui-px-2 sui-rounded sui-border sui-border-neutral-border sui-border-solid">
              <div className="sui-flex sui-items-center sui-justify-between">
                {!editable && !selectedAccount ? (
                  <span data-testid="SeasonMerchantAccount--no-account-text">No deposit account selected</span>
                ) : (
                  <>
                    <div>
                      <AccountOptionName account={selectedAccount} />
                      <div className="sui-flex sui-items-center sui-mt-0.5">
                        <AccountOptionLastFour account={selectedAccount} />
                        <AccountOptionStatus account={selectedAccount} />
                      </div>
                    </div>
                    {editable && (
                      <div className="sui-flex sui-items-center sui-justify-end">
                        <LabelButton
                          variantType="tertiary"
                          data-testid="SeasonMerchantAccount--change-button"
                          onClick={() => setShowEdit(true)}
                          className={`${disabled ? 'sui-cursor-default' : ''}`}
                          disabled={disabled}
                        >
                          Change
                        </LabelButton>
                        {(selectedAccount?.status === AccountStatus.APPROVED ||
                          selectedAccount?.status === AccountStatus.RESTRICTED_SOON ||
                          selectedAccount?.status === AccountStatus.INFO_NEEDED ||
                          selectedAccount?.status === AccountStatus.BANK_DETAILS_REQUIRED) && (
                          <LabelButton
                            variantType="secondary"
                            data-testid="SeasonMerchantAccount--connect-button"
                            onClick={() => onVerifyOrConnect()}
                            className={`sui-ml-1 ${disabled ? 'sui-cursor-default' : ''}`}
                            disabled={disabled}
                          >
                            {selectedAccount?.status === AccountStatus.APPROVED && 'Update account'}
                            {(selectedAccount?.status === AccountStatus.INFO_NEEDED ||
                              selectedAccount?.status === AccountStatus.RESTRICTED_SOON) &&
                              'Verify account'}
                            {selectedAccount?.status === AccountStatus.BANK_DETAILS_REQUIRED && 'Connect account'}
                          </LabelButton>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default SeasonMerchantAccount;
